import { useEffect, useState } from "react";
import {
  getRentalConditions,
  getVoucherRentalConditions,
} from "../api/services/rentalConditionService";

export const RentalConditionsModal = ({
  isOpen,
  onClose,
  searchId,
  offerHash,
  reservationNo,
}) => {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    const fetchTerms = async () => {
      setLoading(true);
      setTerms(null);
      setOpenSections({});

      try {
        let data;

        if (reservationNo) {
          data = await getVoucherRentalConditions(reservationNo);
        } else if (searchId && offerHash) {
          data = await getRentalConditions(searchId, offerHash);
        }

        setTerms(
          data?.rental_conditions || data?.data?.rental_conditions || [],
        );
      } catch (error) {
        console.error("Failed to fetch rental conditions:", error);
        setTerms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, [isOpen, searchId, offerHash, reservationNo]);

  if (!isOpen) return null;

  const sortedTerms = terms ? [...terms].sort((a, b) => a.order - b.order) : [];

  const toggleSection = (order) => {
    setOpenSections((prev) => ({
      ...prev,
      [order]: !prev[order],
    }));
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] border border-slate-100 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white px-6 py-5 border-b border-slate-100">
          <h2 className="font-heading text-primary font-bold text-xl">Rental Terms</h2>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-primary rounded-full animate-spin" />
              <p className="font-heading text-sm font-medium">Loading…</p>
            </div>
          ) : !sortedTerms.length ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              No rental conditions available.
            </div>
          ) : (
            <div className="space-y-3">
              {sortedTerms.map((section) => {
                const isOpen = openSections[section.order] || false;

                return (
                  <div
                    key={section.order}
                    className="rounded-xl border border-primary bg-slate-50 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection(section.order)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left hover:bg-white transition-colors"
                    >
                      <span className="text-sm font-bold text-slate-900">
                        {section.name}
                      </span>

                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary text-lg font-bold">
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="border-t border-primary bg-white px-4 py-4 space-y-4">
                        {[...section.content]
                          .sort((a, b) => a.order - b.order)
                          .map((item) => {
                            if (!item.content || item.content.trim() === "") {
                              return null;
                            }

                            return (
                              <div
                                key={item.order}
                                className="rounded-xl border border-primary bg-slate-50 p-4"
                              >
                                <p className="text-sm font-bold text-slate-900 mb-1">
                                  {item.name}
                                </p>

                                <p className="text-sm leading-6 text-slate-700">
                                  {item.content}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-4 flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="bg-primary hover:bg-primary-hover text-white font-bold px-6 py-2.5 rounded-xl text-sm transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
